import { BaseCemsReadingComponent } from "./reading";
import { ChartOnclick } from "@cems-eco/core/src/models";
import { ReadingData } from "../../services";
import formatter from "@cems-eco/core/src/services/formatter/formatter";
import { Options } from "vue-class-component";
import { ElLoading } from "element-plus";
import moment from "moment";
import { ReadingComponent } from "./interfaces";
import { getColorFromUUID } from "@/utils";

@Options({
  props: {
    equipment: Object,
    type: String,
  },
})
export class CloudReadingComponent
  extends BaseCemsReadingComponent
  implements ReadingComponent
{
  subscription: any = {
    oneSec: null,
    thirtyMinute: null,
  };
  async created() {
    this.setChartConfig();
    this.setChartOneMinConfig();
  }

  async mounted() {
    const userStorage = await this.monitorBloc.getUserStorage();
    this.subscription.thirtyMinute = (
      await this.monitorBloc.listenReadings30m(
        userStorage.id,
        this.equipment.id
      )
    ).subscribe(this.listenReadings30m);
    this.subscription.oneSec = (
      await this.monitorBloc.listenReadings1s(userStorage.id, this.equipment.id)
    ).subscribe(this.listenReadings1s);
  }

  async unmounted() {
    this.subscription.thirtyMinute?.unsubscribe();
    this.subscription.oneSec?.unsubscribe();
    this.monitorBloc.stopReadings();
  }

  getRandomNumber(total) {}

  async fetch() {
    const sensors = await this.monitorBloc.getSensorByEquipment(
      this.equipment.id
    );
    const data: ReadingData[] = await this.monitorBloc.getReadings(
      this.equipment.id
    );

    this.labels = [...new Set(data.flatMap((d) => d.created_at))].reverse();
    const colors: any = {};
    this.datasets = sensors.map((sensor, index) => {
      colors[sensor.id] = getColorFromUUID(sensor.id, 0, this.equipment.id, index);
      return {
        id: sensor.id,
        label: sensor.name,
        borderColor: colors[sensor.id],
        fill: false,
        labels: JSON.parse(JSON.stringify(this.labels)),
        data:
          this.labels.length < 1
            ? []
            : this.labels
                .map((created_at) => {
                  const r = data.find(
                    (d) => d.created_at == created_at && d.id == sensor.id
                  );
                  return r != undefined ? r.value : 0;
                })
                .filter((d) => d != null),
      };
    });

    this.config.data = {
      labels: this.labels.map((l) => formatter.shortTime(l)),
      datasets: this.datasets,
    };

    this.equipment.sensors = sensors.map((sensor) => ({
      id: sensor.id,
      last_reading: new Date(Date.now() - 65_000).getTime(),
      name: sensor.name,
      unit: sensor.unit,
      value: "-",
      alarm: false,
      message: "",
      color: colors[sensor.id],
    }));
  }

  setChartConfig() {
    this.config = {
      type: "line",
      data: {
        labels: this.labels,
        datasets: this.datasets,
      },

      // Configuration options go here
      options: {
        responsive: true,
        aspectRatio: 10 / 4,
        maintainAspectRatio: this.type == "basic",
        layout: {
          padding: {
            top: 7,
            right: 7,
          },
        },
        legend: {
          display: this.type == "advance",
          labels: {
            fontColor: this.type == "basic" ? "#fff" : "#000",
          },
        },
        scales: {
          yAxes: [
            {
              ticks: {
                display: true,
                fontColor: "#BEBEBE",
                fontSize: 13,
                padding: 12,
              },
              gridLines: {
                borderDash: [8, 4],
                zeroLineWidth: 0,
                drawBorder: false,
                display: this.type == "advance",
              },
            },
          ],
          xAxes: [
            {
              ticks: {
                display: true,
                fontColor: "#BEBEBE",
                fontSize: 13,
              },
              gridLines: {
                borderDash: [8, 4],
                zeroLineWidth: 0,
                drawBorder: false,
                display: this.type == "basic",
              },
            },
          ],
        },
      },
    };
  }

  setChartOneMinConfig() {
    this.oneMinConfig = {
      type: "line",
      data: {
        labels: this.oneMinLabels,
        datasets: this.oneMinDatasets,
      },

      // Configuration options go here
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          yAxes: [
            {
              ticks: {
                display: this.type == "advance",
                fontColor: "#BEBEBE",
                fontSize: 13,
                padding: 12,
              },
              gridLines: {
                borderDash: [8, 4],
                zeroLineWidth: 0,
                drawBorder: false,
                display: this.type == "advance",
              },
            },
          ],
          xAxes: [
            {
              ticks: {
                display: this.type == "advance",
                fontColor: "#BEBEBE",
                fontSize: 13,
              },
              gridLines: {
                borderDash: [8, 4],
                zeroLineWidth: 0,
                drawBorder: false,
                display: this.type == "basic",
              },
            },
          ],
        },
      },
    };
  }

  reading() {}

  async min30Onclick(res: ChartOnclick) {
    if (this.type == "basic") return;

    const sensor_id = res.dataset.id;

    const timestamp = moment(res.dataset.labels[res.index]).format(
      "YYYY-MM-DD HH:mm:ss"
    );
    const data = await this.monitorBloc.getOneMinBy30min(sensor_id, timestamp);
    const dataName = [...new Set(data.flatMap((d) => d.name))].sort(
      (a: any, b: any) => a.localeCompare(b)
    );

    this.oneMinLabels = [
      ...new Set(data.flatMap((d) => d.created_at)),
    ].reverse();

    this.oneMinDatasets = dataName.map((name: any) => ({
      id: data.find((d) => d.name.toLowerCase() == name.toLowerCase())!.id,
      label: name,
      borderColor: res.dataset.borderColor,
      fill: false,
      data: this.oneMinLabels
        .map((created_at) => {
          const r = data.find(
            (d) =>
              d.created_at == created_at &&
              d.name.toLowerCase() == name.toLowerCase()
          );
          return r != undefined ? r.value : 0;
        })
        .filter((d) => d != null),
    }));

    this.oneMinConfig.data = {
      labels: this.oneMinLabels.map((l) => formatter.shortTime(l)),
      datasets: this.oneMinDatasets,
    };
    this.isShowOneMin = true;
  }

  backTo30min() {
    this.isShowOneMin = false;
  }
}
